import { css, cx } from '@emotion/css';
import { Card, CardHeader, Theme, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { PatientEvent } from '@/models/PatientEventModel';
import { doctorFormattedName } from '@/models/UserModel';
import { Queries } from '@/queries/Queries';
import { toDateTime } from '@/utils/dateUtils';

type PatientEventProps = {
  event: PatientEvent;
};

const getEventWording = (
  event: PatientEvent,
  isAuthorSelf: boolean,
  t: TFunction<'translation', undefined>,
) => {
  const doctorName = doctorFormattedName(
    event.author_last_name,
    event.author_first_name,
  );

  const typeMessageKey = isAuthorSelf ? 'self' : 'other';

  if (event.event_type == 'doctor_decided_not_to_renew') {
    const beforAfterCp = event.payload?.stopped_before_cp_end
      ? 'beforeEndCP'
      : 'afterEndCP';
    return t(
      `patientEvents.decidedNotToRenew.${beforAfterCp}.${typeMessageKey}`,
      {
        doctorName,
      },
    );
  }

  if (event.event_type == 'careplan_renewed') {
    return t(`patientEvents.carePlanRenewed.${typeMessageKey}`, {
      doctorName,
    });
  }

  return '';
};

export const PatientEventCard = ({ event }: PatientEventProps) => {
  const me = Queries.user.useMe({});
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const isAuthorSelf = me?.id === event.author_id;
  const eventText = getEventWording(event, isAuthorSelf, t);

  const timeSent = toDateTime(event.created_at).toLocaleString(
    DateTime.TIME_SIMPLE,
  );

  return (
    <Card elevation={0} className={cx(styles.card)}>
      <CardHeader
        className={cx(styles.headerContainer)}
        title={
          <>
            <Typography className={styles.title}>
              {t('patientEvents.header')}
            </Typography>
            <Typography variant="caption">{timeSent}</Typography>
          </>
        }
      />
      <Typography variant="body" className={styles.bodyContainer}>
        {t(eventText)}
      </Typography>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  }),
  senderContainer: css({
    justifyContent: 'flex-end',
  }),
  card: css({
    display: 'flex',
    flexDirection: 'column',
    width: '85%',
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    gap: theme.spacing(4),
  }),
  practitionerOnlyCard: css({
    backgroundColor: theme.palette.green.contrastText,
  }),
  senderCard: css({
    borderRadius: theme.spacing(4, 4, 0, 4),
  }),
  unreadCard: css({
    border: '1px solid',
    borderColor: `rgb(from ${theme.palette.primary.main} r g b / 0.2)`,
  }),
  headerContainer: css({
    padding: 0,
  }),
  title: css({
    fontWeight: 'bold',
    marginRight: theme.spacing(4),
    fontStyle: 'italic',
  }),
  bodyContainer: css({
    whiteSpace: 'pre-wrap',
    maxWidth: '100%',
    fontStyle: 'italic',
  }),
});
